import { v4 as uuidv4 } from 'uuid';
import type {
  AnalyticsContext,
  ExperimentServiceVariantServer,
  ExperimentSource,
} from '../../types';
import { setCookie, getCookie } from '../../utils/cookies';
import { getBaseUrl } from '../../utils/requests';
import amplitude from '../amplitude';
import { isDatadogSyntheticTest } from '../datadog';

export const UUID_COOKIE_NAME = 'whoop-experiment-uuid';

const experimentServiceVariants: Record<string, string> = {};

const fetchVariant = async ({ filters, name, headers }) => {
  if (isDatadogSyntheticTest()) return null;

  const body = filters ? JSON.stringify(filters) : undefined;
  try {
    const variantResponse = await fetch(
      `${getBaseUrl()}/experiment-service/v1/experiments/name/${name}/variant`,
      {
        method: 'POST',
        headers,
        body,
      },
    );
    if (variantResponse.status !== 200) {
      return null;
    }

    const variant = await variantResponse.text();
    return variant;
  } catch (error) {
    return null;
  }
};

export const getCleanCookie = (cookie: string) => {
  let cleanCookie;
  const decodedCookie = decodeURIComponent(cookie);
  try {
    const parsedCookie = JSON.parse(decodedCookie);
    cleanCookie = parsedCookie;
  } catch (error) {
    // if it didn't need to be parsed / wasn't valid JSON but we know cookie exists, then use decodedCookie!
    cleanCookie = decodedCookie;
  }
  return cleanCookie;
};

export default {
  getVariant: async (
    name: string,
    filters?: AnalyticsContext,
    uuidParam?: string,
  ) => {
    if (experimentServiceVariants[name]) {
      return experimentServiceVariants[name];
    }
    const cookieValue = getCookie<string>(UUID_COOKIE_NAME);

    let uuid;
    if (uuidParam) {
      uuid = uuidParam;
    } else {
      uuid = cookieValue ? getCleanCookie(cookieValue) : uuidv4();
    }

    const headers = {
      'Content-Type': 'application/json',
      'X-WHOOP-Installation-Identifier': uuid,
    };

    const variant = await fetchVariant({ headers, name, filters });
    if (variant) {
      setCookie<string>(UUID_COOKIE_NAME, uuid);
      if (amplitude.setUserProperty) {
        amplitude.setUserProperty(name, variant);
        amplitude.setUserProperty('experiment_service_id', uuid);
      }
      experimentServiceVariants[name] = variant;
      return variant;
    }

    return null;
  },
  getVariantByUserToken: async (
    name: string,
    userToken: string,
    filters?: AnalyticsContext,
  ) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    };
    const body = filters ? JSON.stringify(filters) : undefined;
    try {
      const variantResponse = await fetch(
        `${getBaseUrl()}/experiment-service/v1/experiments/name/${name}/user-variant`,
        {
          method: 'POST',
          headers,
          body,
        },
      );
      if (variantResponse.status !== 200) {
        return null;
      }
      const variant = await variantResponse.text();
      return variant;
    } catch (_err) {
      return null;
    }
  },
  getVariantOnServer: async ({
    name,
    filters,
    experimentUUID,
  }: ExperimentServiceVariantServer) => {
    const headers = {
      'Content-Type': 'application/json',
      'Accept-Language': 'en',
      // unifies FE and BE experiment uuid
      'X-WHOOP-Installation-Identifier': experimentUUID,
      cache: 'no-store',
    };

    const variant = await fetchVariant({ headers, name, filters });
    return variant ? { variant } : { variant: null };
  },
} as ExperimentSource;
